import { useState, type ReactElement, type ReactNode } from 'react';
import { NavLink } from 'react-router';
import { Knapp } from './Knapp.js';
import { Virksomhetsboks } from './Virksomhetsboks.js';
import { loggUt } from '../auth/oidc.js';

export interface Lenke {
  readonly til: string;
  readonly tekst: string;
  /** Hvor mange oppgaver som venter i modulen. Vises bare når det er noen. */
  readonly antall?: number;
  /**
   * En egen app på samme adresse, som vedlikeholdsappen. Den lastes på nytt i
   * stedet for å rutes her: ruteren her kjenner ikke sidene der.
   */
  readonly egenApp?: boolean;
}

interface RammeProps {
  brukerNavn: string;
  kommuneNavn: string;
  lenker: readonly Lenke[];
  children: ReactNode;
}

/**
 * Page shell: a fixed sidebar on wide screens, a disclosure menu on narrow ones.
 *
 * The sidebar replaced a horizontal tab row because the row had run out of
 * space. Six modules already crowded it and Bemanning is still to come; a
 * vertical list takes another entry without rebalancing anything, and it keeps
 * the module you are in visible while you work.
 *
 * The skip link is still the first focusable element on the page (WCAG 2.4.1),
 * and matters MORE with a sidebar than it did with a tab row: a keyboard user
 * would otherwise tab through every module on every page load. It is visually
 * hidden until focused, which keeps it available without putting it in the way.
 */
export function Ramme({ brukerNavn, kommuneNavn, lenker, children }: RammeProps): ReactElement {
  const [menyApen, settMenyApen] = useState(false);

  const lenkeklasser = ({ isActive }: { isActive: boolean }): string =>
    [
      // Tettere på en stor skjerm, der det er mus og mer å vise ved siden av;
      // full trykkflate på en telefon.
      'relative flex min-h-11 items-center rounded-md px-3 py-2 text-sm transition-colors lg:min-h-9 lg:px-2.5 lg:py-1.5 lg:text-[0.875rem]',
      'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-merke',
      // Det aktive valget er en hvit flate med en blå strek: tydelig nok til å
      // finne igjen, rolig nok til at blikket går til innholdet.
      isActive
        ? 'bg-meny-aktiv font-semibold text-merke-sterk shadow-sm before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-merke'
        : 'text-tekst hover:bg-meny-hover',
    ].join(' ');

  const telling = (lenke: Lenke) =>
    lenke.antall ? (
      <span className="ml-auto rounded-full bg-varsel-svak px-2 py-0.5 text-xs font-semibold text-tekst">
        {lenke.antall}
        <span className="sr-only">
          {lenke.antall === 1 ? ' oppgave venter' : ' oppgaver venter'}
        </span>
      </span>
    ) : null;

  const meny = (
    <ul className="flex flex-col gap-1 lg:gap-0.5">
      {lenker.map((lenke) => (
        <li key={lenke.til}>
          {lenke.egenApp === true ? (
            <a href={lenke.til} className={lenkeklasser({ isActive: false })}>
              {lenke.tekst}
              {telling(lenke)}
            </a>
          ) : (
            <NavLink
              to={lenke.til}
              end={lenke.til === '/'}
              // aria-current tells assistive technology which page is open; the
              // filled background alone would only tell sighted users.
              className={lenkeklasser}
              onClick={() => {
                settMenyApen(false);
              }}
            >
              {lenke.tekst}
              {telling(lenke)}
            </NavLink>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    // Ved utskrift står bare innholdet igjen: ingen meny, og ingen fast høyde
    // som ville kuttet en rapport etter første side.
    <div className="h-screen overflow-hidden bg-flate-dempet max-lg:h-auto max-lg:min-h-screen max-lg:overflow-visible print:h-auto print:overflow-visible print:bg-white">
      <a
        href="#innhold"
        className="sr-only print:hidden rounded-md bg-merke px-4 py-2 text-merke-tekst focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50"
      >
        Hopp til hovedinnhold
      </a>

      {/* MOBILFØRST SNUDD PÅ HODET, MED VILJE. Kolonnen er standard og
          sammenslåingen er unntaket, ikke omvendt. Skrevet mobilførst var en
          vannrett stripe øverst det oppsettet man falt tilbake til hvis en
          eneste `lg:`-klasse ikke kom med — og det er akkurat den feilen som
          ble meldt. Nå er det motsatte sant: faller en `max-lg:`-klasse bort,
          blir resultatet en sidekolonne på en smal skjerm. Rart, men riktig
          vei å feile. */}
      <div className="flex h-full max-lg:block max-lg:h-auto print:block print:h-auto">
        {/* The sidebar is a full-height column: navigation at the top, the person
            and the way out pinned to the bottom.
 
            Neither sticky nor fixed. It was sticky AND h-screen, which puts it in
            its own compositing layer, and browsers do not always repaint that
            layer when the viewport itself changes size — going fullscreen left
            the old paint behind and drew the new one beside it, so the menu
            appeared twice. Fixed fixed that but made the layout depend on a
            handful of new utilities all landing: miss one and the column
            collapses into a band across the top instead of degrading to a side
            column.
 
            So the shell is simply a full-height flex row. Only the CONTENT
            column scrolls; the sidebar is overflow-hidden and cannot, so there
            is no second scrollbar and no empty space to scroll the menu down
            into. The link list may scroll inside itself, but only if there are
            more links than fit — otherwise «Logg ut» would become unreachable
            on a short window. There is no compositing layer to leave behind, and
            if any single utility failed the row would still be a row. */}
        <div className="relative flex h-full w-56 shrink-0 flex-col overflow-hidden border-r border-meny-kant bg-meny max-lg:h-auto max-lg:w-full max-lg:overflow-visible max-lg:border-r-0 print:hidden">
          <div className="flex shrink-0 items-center justify-between gap-4 bg-meny px-4 py-3 max-lg:border-b max-lg:border-meny-kant">
            <div>
              <p className="text-base font-semibold text-tekst">Enklere styring</p>
              <p className="text-xs text-tekst-dempet">{kommuneNavn}</p>
            </div>
            <button
              type="button"
              aria-expanded={menyApen}
              aria-controls="hovedmeny"
              onClick={() => {
                settMenyApen(!menyApen);
              }}
              className="hidden min-h-11 rounded-md border border-meny-kant bg-meny-aktiv px-3 text-sm text-tekst focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-merke max-lg:block"
            >
              {menyApen ? 'Lukk meny' : 'Meny'}
            </button>
          </div>

          {/* One disclosure region holding both the links and the account block:
              on a narrow screen «Logg ut» belongs behind the same button as the
              navigation, not on a row of its own. */}
          <div
            id="hovedmeny"
            className={`flex min-h-0 flex-1 flex-col bg-meny ${
              menyApen ? 'max-lg:flex' : 'max-lg:hidden'
            } max-lg:flex-none max-lg:border-b max-lg:border-meny-kant`}
          >
            <Virksomhetsboks />

            {lenker.length > 0 ? (
              <nav
                aria-label="Hovedmeny"
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2"
              >
                {meny}
              </nav>
            ) : null}

            <div className="mt-auto shrink-0 border-t border-meny-kant px-4 py-3">
              <p className="text-xs text-tekst">{brukerNavn}</p>
              <Knapp
                variant="sekundar"
                className="mt-1.5 w-full lg:min-h-9 lg:py-1.5"
                onClick={() => void loggUt()}
              >
                Logg ut
              </Knapp>
            </div>
          </div>
        </div>

        {/* `relative` er ikke pynt. `sr-only` er `position: absolute`, og uten en
            posisjonert forelder løses de mot dokumentet i stedet for mot denne
            kolonnen. Da vokser dokumentet med rullehøyden — 1197 px mot et
            vindu på 560 — og siden kan rulles ned i tomrommet under skallet.
            Det var den hvite stripen. */}
        <div className="relative h-full min-w-0 flex-1 overflow-y-auto overscroll-contain max-lg:h-auto max-lg:overflow-visible print:h-auto print:overflow-visible">
          {/* `<main>` with an id the skip link targets, and tabIndex -1 so focus
              can actually land there when the link is followed.

              Bredden: tomrommet på hver side er halvparten av det et fast tak
              ville gitt. `calc(50% + 56rem)` er 112rem (1792 punkter) når
              kolonnen er nettopp så bred, og halvveis mellom 112rem og full
              bredde når den er bredere — så en 2560-skjerm mister halvparten
              av tomrommet, mens en 1920-skjerm, der det ikke var noe, ikke
              merker noen forskjell. Det første taket var 80rem, og da sto
              tabellene med mange kolonner og rullet sidelengs ved siden av
              ubrukt plass. Løpende tekst har sin egen lesebredde
              (`max-w-3xl`) og blir ikke lengre. */}
          <main
            id="innhold"
            tabIndex={-1}
            className="mx-auto max-w-[calc(50%_+_56rem)] px-6 py-8 xl:px-8 print:max-w-none print:p-0"
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
